import { useState, useCallback } from "react";
import { apiHandler } from "../../api/index";
import { endpoint } from "../../api/endpoint";
import { useSelector } from "react-redux";

export const useBackgroundUpload = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const token = useSelector((state) => state.auth.token);

  const uploadBackground = useCallback(
    async (formData) => {
      setIsLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const response = await apiHandler({
          url: endpoint.UPLOAD_BACKGROUND,
          method: "POST",
          data: formData,
          authToken: { token },
          headers: {
            // Let axios handle Content-Type for FormData
            "Content-Type": undefined,
          },
        });

        if (response.status === 200 || response.status === 201) {
          setSuccess(true);
          setIsLoading(false);
          return { success: true, data: response.data };
        } else {
          // Extract error message from various possible response structures
          const errorMessage =
            response.data?.error_message ||
            response.data?.message ||
            response.data?.error ||
            `Server error: ${response.status}`;
          
          console.error("Upload failed with status:", response.status, "Response:", response.data);
          setError(errorMessage);
          setIsLoading(false);
          return { success: false, error: errorMessage };
        }
      } catch (err) {
        const errorMessage = err.message || "An error occurred during upload";
        console.error("Upload exception:", err);
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [token]
  );

  const updateBackground = useCallback(
    async (backgroundId, data) => {
      if (!backgroundId || backgroundId === "null" || backgroundId === "undefined") {
        const errorMsg = "A valid positive integer backgroundId is required";
        console.error("updateBackground called with invalid backgroundId:", backgroundId);
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      setIsLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const response = await apiHandler({
          url: `${endpoint.UPDATE_BACKGROUND}/${backgroundId}`,
          method: "PUT",
          data,
          authToken: { token },
          headers: {
            // Let axios set the multipart boundary for FormData
            "Content-Type": undefined,
          },
        });

        if (response.status === 200 || response.status === 201) {
          setSuccess(true);
          setIsLoading(false);
          return { success: true, data: response.data };
        } else {
          const errorMessage =
            response.data?.error_message ||
            response.data?.message ||
            response.data?.error ||
            `Server error: ${response.status}`;
          console.error("Update failed with status:", response.status, "Response:", response.data);
          setError(errorMessage);
          setIsLoading(false);
          return { success: false, error: errorMessage };
        }
      } catch (err) {
        const errorMessage = err.message || "An error occurred during update";
        console.error("Update exception:", err);
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [token]
  );

  const resetState = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return {
    uploadBackground,
    updateBackground,
    isLoading,
    error,
    success,
    resetState,
  };
};
